import { Addendums } from "./Addendum";
import { Applicant, ApplicantType } from "./Applicant";
import { ApplicationConfiguration } from "./Redux";
import { HelpRequest } from "./HelpRequests";

export interface ApplicationCharge {
  label: string;
  chgCode: string;
  amount: number;
  header: string;
  caption?: string;
}

export interface AppliedConcession {
  id: string;
  title: string;
  description: string;
  isAutomated: boolean;
  discount: {
    amount: number;
    chargeCode: string;
  };
  terms: number[];
  moveInStart: string | null;
  moveInEnd: string | null;
}

export interface GroupScreeningStatusDetails {
  message?: string | null;
  description?: string | null;
  statusLabel?: string | null;
  status?: string | null;
  [key: string]: unknown;
}

// Type coming from GET /Applications/{applicationId} API
export interface Application {
  // Legacy applications use a numeric id; Lease Domain applications use a UUID string
  // (see AppLease.AdminDashboard.API's isLeaseDomainApplicationId/mapLeaseDomainApplication).
  readonly applicationId: number | string;
  unitId: string;
  unit: number | null;
  primaryApplicantFirstName: string;
  primaryApplicantLastName: string;
  applicationStatus: ApplicationStatusText;
  applicationStatusId: ApplicationStatusId;
  identityScreeningId: number;
  creditScreeningOutcome: null;
  communityId: string;
  phaseId: string;
  leaseId?: string;
  buildingId: string;
  communityName: string;
  createdDate: string;
  modifiedDate: string;
  moveInDate: string;
  leaseGeneratedDate: string;
  leaseTerm: number;
  applicationScreeningResult: string;
  applicationScreeningResultId: number;
  adminDashApplicationScreeningResult?: string;
  adminDashApplicationScreeningResultId?: number;
  applicants: Applicant[];
  dependents: any[];
  monthlyCharges: ApplicationCharge[];
  oneTimeCharges: ApplicationCharge[];
  applicationHold: ApplicationHold | null;
  parking?: Parking | null;
  lease: Lease;
  addendums?: Addendums | null; // null if application is not in the state, {} if no addendums for application and filled if available
  // AP2-6943: Lease Domain only; null before approval.
  addendumStatus?: "NotRequired" | "Pending" | "Completed" | null;
  isQuickMoveIn?: boolean;
  isSfQuickMoveIn?: boolean;
  isDocumentScreeningRequired?: boolean;
  // Lease Domain-only: legacy has no equivalent, its cancel eligibility is purely status-based.
  // false while lease generation holds it, true again if generation fails before an envelope
  // exists and MRI Finish was never attempted (if Finish may have run, it stays false and the
  // application is locked), undefined for a legacy application.
  canAssociateCancelApplication?: boolean;
  // Lease Domain-only: true until the application is approved (incl. approval with conditions met) or MRI takes over, undefined for a legacy application.
  canResolveAlerts?: boolean;
  // Lease Domain-only: server-computed Finish eligibility, undefined for a legacy application.
  canHideFromAssociates?: boolean;
  // Lease Domain-only: plain-English hover text for the Lease Generation Failed status.
  leaseGenerationFailureDescription?: string | null;
  // Lease Domain-only: why Generate Lease is closed under a full unprocessable lock, null when not locked.
  generationLockedReason?: string | null;
  // Lease Domain-only: why add-ons and charges are closed under a partial (MRI-cause) lock, null when not partially locked.
  addOnsUnavailableReason?: string | null;
  hasHousingVoucher?: boolean;
  isAffordable?: boolean;
  // AP2-6877: itemized into an independent unit line and an optional pet/addon deposit-increase
  // line (null when the application has no pet/addon SEC charge), each with its own
  // requiredAmount/paidAmount - replacing one combined {requiredAmount, paidAmount} so AD can show
  // Paid/Not Paid per line instead of a single figure that masked an abandoned payment attempt.
  leaseSecurityDeposit?: {
    // Legacy-only flat shape (no unit/pet split), AP2-7017.
    requiredAmount?: number;
    paidAmount?: number;
    unitDeposit?: {
      requiredAmount: number | undefined;
      paidAmount: number | undefined;
    };
    petDeposit?: {
      requiredAmount: number | undefined;
      paidAmount: number | undefined;
    } | null;
  };
  configuration?: ApplicationConfiguration;
  cancelledBy: number;
  // Lease Domain-only: legacy has no cancelReason concept and never populates this.
  // Lease Domain's cancel_reason_cd lookup ('ApplicantHighRisk' etc.) is the real source
  // of "why was this cancelled" - legacy instead overloads cancelledBy with a fake
  // negative sentinel (CancelledDueToHighRisk = -4) for the same purpose.
  cancelReason?: string | null;
  groupScreeningStatusDetails?: string | GroupScreeningStatusDetails;
  isGuarantorOnly?: boolean;
  appliedConcessions: AppliedConcession[];
  areAppliedConcessionsInMri: boolean;
  // AP2-6506: Lease Domain-only - GET /leases/applications now returns helpRequests[] inline (the
  // same shape POST /admin/help-requests/bulk-fetch returns), so the details page can read this
  // directly for a Lease Domain (UUID) application instead of a separate bulk-fetch call. Legacy
  // applications never populate this field - they still get help requests only via bulk-fetch.
  helpRequests?: HelpRequest[];
}

// Type after data has been processed by redux reducers
export interface ReduxApplication extends Application {
  readyToFinishCount?: number;
  completeUnitId?: string;
  lastFetchedDate?: string;
  incStaleItemsCount?: number;
  helpRequestCount?: number;
}

export interface GetApplicationsBody {
  applications: Application[] | ReduxApplication[];
  count: number;
}

export interface LeaseEvent {
  id: number;
  createdDate: string;
  expirationDate: string;
  applicationId: number | string;
  leaseEventId: number;
  refId: string;
  esignStatus: string;
  dsDocPath: string;
}

export interface PostLeaseResponse {
  expirationDate: string; // "2023-11-10T20:31:38.078Z"
  applicationId: number | string;
  leaseEventId: number;
  esignStatus: string;
  dsDocPath: string; // url for the docusign document
}

export interface Lease {
  // Legacy-only field (application.applicationStatusId-driven, see LeaseProcess.tsx's
  // getPrimaryApplicantCanGenerateLease fallback). Lease Domain applications instead use the
  // primary applicant's real controls.canGenerateLease.
  canGenerateLease: boolean;
  // AP2-6229-followup: sourced from Lease Domain's own lease + latest DocuSign package status
  // (true only while leaseStatus is "Scheduled" and the package is still "Sent" - not yet
  // Signed/Voided/Declined/Expired, and not yet regenerated - see LeaseDomain.API's
  // get-lease-application.ts), passed through verbatim by AdminDashboard.API for Lease Domain
  // (UUID) applications. Legacy (numeric-id) applications source this from
  // application.applicationStatusId == LEASE_PENDING instead (see postLease/LeaseEventId.LeaseResent).
  canResendLease: boolean;
  // AP2-6379: sourced from Lease Domain's own lease + latest DocuSign package status (true only
  // while leaseStatus is "Scheduled" and the package is still "Sent" - shares its condition with
  // canResendLease, since DocuSign only allows voiding/resending an envelope that hasn't
  // completed yet - see LeaseDomain.API's get-lease-application.ts), passed through verbatim by
  // AdminDashboard.API for Lease Domain (UUID) applications. Legacy (numeric-id) applications
  // don't have this field at all yet, so it's always false/undefined for them today.
  canVoidLease: boolean;
  leaseEvents: LeaseEvent[];
  // Lease Domain-only fields (AppLease.AdminDashboard.API's `lease: application.lease ?? null`
  // raw passthrough of Lease Domain's own lease object) - legacy never populates these.
  leaseId?: string;
  leaseStatus?: string;
  docusignPackageId?: string | null;
  // signature_status_cd of the docusignPackageId package (Created/Sent/Signed/Voided/Declined/Expired/...)
  signatureStatus?: string | null;
  // AP2-6912: signed lease PDF URL (60 min CloudFront-signed), null until the lease is Signed
  signedDocumentUrl?: string | null;
}

// AP2-6379: confirmed against AdminDashboard.API's post-void-documents.js handler
// (POST /application/{applicationId}/void-documents). Despite LeaseDomain returning a
// structured list of failed packageIds/refIds internally, AdminDash-API collapses that into a
// single `error` string rather than passing a failedItems array through to the client.
export interface VoidLeaseResponse {
  message?: string; // present on success (200)
  error?: string; // present on failure (400/500)
}

export interface Parking {
  optInApplicantId?: number | null; // returned with reservation
  optOutApplicantId?: number | null; // returned without reservation
  reservationUrl?: string | null; // returned with reservation
}

export interface ApplicationHold {
  id: number;
  applicationId: number | string;
  holdDate: string;
  updateMins: number;
  updateEventId: ApplicationHoldEventId;
  updateEvent: string;
  updateOperationId: 2;
  updateOperation: string;
  createdDate: string;
  associateId: any | null;
  isExpired: boolean;
  isClockRunning?: boolean;
  isExtendable?: boolean;
}

export enum ApplicationHoldEventId {
  NEW_APPLICATION = 1,
  APPROVED_APPLICATION = 2,
  CANCELLED_APPLICATION = 3,
  SCREENING_APPLICATION = 4,
  CONTINUE_APPLICATION = 5,
  ADMIN_ADJUSTED = 6,
}

export interface ApplicationProcess {
  id: number;
  applicationId: number | string;
  applicationStatusId: ApplicationStatusId;
  applicationStageId: ApplicationStage;
  parentApplicantId: number;
  applicantTypeId: ApplicantType;
  createdDate: string;
  modifiedDate: string;
}

export enum ApplicationStage {
  ApplicantInformation = 1,
  OccupantsAndGuarantors,
  TermsAndCondition,
  Review,
  PayAndSubmit,
  TwoStepScreening,
  Addendums,
  Addons,
  SignLease,
}

export enum ApplicationStatusText {
  IN_PROGRESS = "In Progress",
  REJECTED = "Rejected",
  COMPLETED = "Completed",
  REMOVED = "Removed",
  CREDIT_AND_ID_RETURNED = "Credit And ID Returned",
  LEASE_PENDING = "Lease Pending",
  LEASE_SIGNED = "Lease Signed",
  CREDIT_AND_ID_PENDING = "Credit And ID Pending",
  CANCELLED = "Cancelled",
  APPROVED = "Approved",
  CLOSED = "Closed",
  INVITE_SENT = "Invite Sent",
  ACTION_REQUIRED = "Action Required",
  APPLICATION_COMPLETE = "Application Complete",
  LEASE_READY = "Lease Ready",
  LEASE_DECLINED = "Lease Declined",
  LEASE_EXPIRED = "Lease Expired",
  LEASE_VOIDED = "Lease Voided",
  LEASE_GENERATION_FAILED = "Lease Generation Failed",
  SCREENING_RETURNED = "Screening Returned",
  SCREENING_PENDING = "Screening Pending",
  PENDING_ADDENDUMS = "Pending Addendums",
}

export enum ApplicationStatusId {
  InviteSent = 0,
  InProgress,
  Rejected,
  Completed,
  Removed,
  CreditAndIDReturned,
  LeasePending,
  LeaseSigned,
  CreditAndIDPending,
  Cancelled,
  Approved,
  Closed,
  ActionRequired,
  ApplicationComplete,
  LeaseReady,
  LeaseExpired,
  LeaseDeclined,
  LeaseGenerationFailed,
}

export enum LeaseEventId {
  LeaseExpired = 1,
  LeaseDeclined,
  LeaseGenerated,
  LeaseIDSet,
  LeaseReady,
  LeaseSigned,
  EsignEvent,
  LeaseResent,
}
